import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import EmployeeManagement from './components/EmployeeManagement';
import AttendanceManagement from './components/AttendanceManagement';
import LeaveManagement from './components/LeaveManagement';
import PayrollManagement from './components/PayrollManagement';
import TaskManagement from './components/TaskManagement';
import PerformanceManagement from './components/PerformanceManagement';
import DocumentManagement from './components/DocumentManagement';
import AssetManagement from './components/AssetManagement';
import AdminPanel from './components/AdminPanel';
import Login from './components/Login';
import RoleManagement from './components/RoleManagement';
import LeadManagement from './components/LeadManagement';
import FollowUpNotification from './components/FollowUpNotification';
import TestFollowUpButton from './components/TestFollowUpButton';
import DummyDashboard from './components/DummyDashboard';
import DailyReport from './components/DailyReport';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      Loading...
    </div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Permission-based Route Component
const PermissionRoute = ({ children, permission }) => {
  const { currentUser, loading, hasPermission } = useAuth();
  
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      Loading...
    </div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Dashboard Router: shows full Dashboard or DummyDashboard based on permission
const DashboardRouter = () => {
  const { hasPermission } = useAuth();
  return hasPermission('dashboard_view') ? <Dashboard /> : <DummyDashboard />;
};

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentModule, setCurrentModule] = useState('Dashboard');
  const { currentUser } = useAuth();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // If not logged in, show only login route
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app">
      <Sidebar 
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar}
        setCurrentModule={setCurrentModule}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />
      <div className={`main-content ${!sidebarOpen ? 'sidebar-closed' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header 
          toggleSidebar={toggleSidebar} 
          currentModule={currentModule}
        />
        <div className="content-container">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            } />
            <Route path="/leads" element={
              <PermissionRoute permission="lead_view">
                <LeadManagement />
              </PermissionRoute>
            } />
            <Route path="/employee" element={
              <PermissionRoute permission="employee_view">
                <EmployeeManagement />
              </PermissionRoute>
            } />
            <Route path="/attendance" element={
              <PermissionRoute permission="attendance_view">
                <AttendanceManagement />
              </PermissionRoute>
            } />
            <Route path="/leave" element={
              <PermissionRoute permission="leave_view">
                <LeaveManagement />
              </PermissionRoute>
            } />
            <Route path="/payroll" element={
              <PermissionRoute permission="payroll_view">
                <PayrollManagement />
              </PermissionRoute>
            } />
            <Route path="/task" element={
              <PermissionRoute permission="task_view">
                <TaskManagement />
              </PermissionRoute>
            } />
            <Route path="/performance" element={
              <PermissionRoute permission="performance_view">
                <PerformanceManagement />
              </PermissionRoute>
            } />
            <Route path="/document" element={
              <PermissionRoute permission="document_view">
                <DocumentManagement />
              </PermissionRoute>
            } />
            <Route path="/asset" element={
              <PermissionRoute permission="asset_view">
                <AssetManagement />
              </PermissionRoute>
            } />
            <Route path="/admin" element={
              <PermissionRoute permission="all">
                <AdminPanel />
              </PermissionRoute>
            } />
            <Route path="/roles" element={
              <PermissionRoute permission="role_manage">
                <RoleManagement />
              </PermissionRoute>
            } />
            <Route path="/daily-report" element={
              <ProtectedRoute>
                <DailyReport />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
      {/* Follow-up Notification System */}
      <FollowUpNotification />
      {/* Test Button for Debugging */}
      <TestFollowUpButton />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
